import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import { BrandingContext } from "../../context/BrandingContext";
import { Routes, API_ROUTE, WEBSITE } from "../../helper/Routes";
import type { FooterMenuItem } from "../../helper/interface";
import { FOOTER_MENU_UPDATED_EVENT } from "../../helper/footerMenuEvents";
import FooterContentModal from "./FooterContentModal";
import "./Footer.css";
import FooterGraphic from "./FooterGraphic";

const fallbackFooterItems: FooterMenuItem[] = [
  {
    idFooterMenu: "fallback-privacy",
    label: "Privacy Policy",
    markdownContent: "Privacy Policy content has not been configured yet.",
    sortOrder: 1,
  },
];

const Footer = () => {
  const branding = useContext(BrandingContext)?.branding ?? null;
  const footerGraphicValue = branding?.footerGraphic?.trim() ?? "";
  const footerGraphic =
    footerGraphicValue && footerGraphicValue.toLowerCase() !== "none"
      ? footerGraphicValue
      : null;

  const [footerItems, setFooterItems] = useState<FooterMenuItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<FooterMenuItem | null>(null);
  const [wordpressContentHtml, setWordpressContentHtml] = useState<string | null | undefined>(undefined);
  const [wordpressContentLoading, setWordpressContentLoading] = useState(false);
  const [wordpressContentError, setWordpressContentError] = useState<string | null>(null);
  const contentRequestId = useRef(0);

  useEffect(() => {
    let isMounted = true;

    const loadFooterItems = async () => {
      try {
        const response = await Axios.get<FooterMenuItem[]>(`${API_ROUTE}${Routes.GET_PUBLIC_FOOTER_MENU}`);
        if (isMounted) {
          setFooterItems(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error("Failed to load footer items", error);
        if (isMounted) {
          setFooterItems([]);
        }
      }
    };

    const handleFooterMenuUpdated = () => {
      contentRequestId.current += 1;
      setSelectedItem(null);
      setWordpressContentHtml(undefined);
      setWordpressContentLoading(false);
      setWordpressContentError(null);
      void loadFooterItems();
    };

    void loadFooterItems();
    window.addEventListener(FOOTER_MENU_UPDATED_EVENT, handleFooterMenuUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener(FOOTER_MENU_UPDATED_EVENT, handleFooterMenuUpdated);
    };
  }, []);

  const itemsToRender = useMemo(
    () => (footerItems.length > 0 ? footerItems : fallbackFooterItems),
    [footerItems]
  );

  const openFooterItem = async (item: FooterMenuItem) => {
    const requestId = contentRequestId.current + 1;
    contentRequestId.current = requestId;
    setSelectedItem(item);
    setWordpressContentHtml(undefined);
    setWordpressContentError(null);

    if (!item.wordpressPostSlug) {
      setWordpressContentLoading(false);
      return;
    }

    setWordpressContentLoading(true);
    try {
      const contentTypes: Array<"post" | "page"> = item.wordpressContentType
        ? [item.wordpressContentType]
        : ["post", "page"];

      for (const [index, contentType] of contentTypes.entries()) {
        try {
          const response = await Axios.get<{ contentHtml: string }>(
            `${WEBSITE}api/footer-content/${contentType}/${encodeURIComponent(item.wordpressPostSlug)}`
          );
          if (contentRequestId.current === requestId) {
            setWordpressContentHtml(response.data.contentHtml);
          }
          return;
        } catch (error) {
          const canTryLegacyPageFallback = index < contentTypes.length - 1
            && Axios.isAxiosError(error)
            && error.response?.status === 404;
          if (!canTryLegacyPageFallback) {
            throw error;
          }
        }
      }
    } catch (error) {
      console.error("Failed to load WordPress footer content", error);
      if (contentRequestId.current === requestId) {
        setWordpressContentError("This content could not be loaded. Please try again later.");
      }
    } finally {
      if (contentRequestId.current === requestId) {
        setWordpressContentLoading(false);
      }
    }
  };

  const closeFooterItem = () => {
    contentRequestId.current += 1;
    setSelectedItem(null);
    setWordpressContentHtml(undefined);
    setWordpressContentLoading(false);
    setWordpressContentError(null);
  };

  return (
    <div className="site-footer-shell">
      {footerGraphic && (
        <FooterGraphic
          variant={footerGraphic}
          className="footer-svg"
        />
      )}

      <footer className="site-footer">
        <section className="footer-grid">
          <article className="footer-copy">
            <div>© {new Date().getFullYear()} FamilySafe®. All rights reserved.</div>
          </article>

          <article className="footer-links">
            {itemsToRender.map((item, index) => (
              <div key={item.idFooterMenu}>
                <button
                  type="button"
                  className="footer-link-button"
                  onClick={() => void openFooterItem(item)}
                >
                  {item.label}
                </button>
                {index < itemsToRender.length - 1 ? (
                  <span className="divider"> - </span>
                ) : null}
              </div>
            ))}
          </article>
        </section>
      </footer>

      <FooterContentModal
        open={selectedItem !== null}
        title={selectedItem?.label ?? ""}
        content={selectedItem?.markdownContent ?? ""}
        wordpressContentHtml={wordpressContentHtml}
        wordpressContentLoading={wordpressContentLoading}
        wordpressContentError={wordpressContentError}
        onClose={closeFooterItem}
      />
    </div>
  );
};

export default Footer;
